import { useState } from "react";

import { useNavigate } from "react-router-dom";

import MenuIcon from "../../../../assets/icons/Menu-icon.png";
import AccessCollinsLogoGenericDark from "../../../../assets/logos/access_Collins_Generic_Dark.svg";
import AccessCollinsLogoGenericLight from "../../../../assets/logos/access_Collins_Generic_Light.svg";
import { Button } from "../Button";

import { getDevice } from "@/utils/device";
import { Calendar, FileText, Headphones, LogOut, Settings } from "lucide-react";
import { useDeviceContext } from "../../../../context/DeviceContext";
import { useIntercomVisibility } from "../../../../hooks/useIntercomVisibility";
import { useUserContext } from "../../../../hooks/useUserContext";
import { dmnIdentity } from "../../../../utils/dmnIdentity";
import { isNativeTab } from "../../../../utils/environment";
import { updateColourScheme } from "../../../../utils/nativeUtils/customActions";
import { useHeaderFlags } from "../../../Layout/HeaderContainer/useHeaderFlags";
import { Avatar, AvatarFallback } from "../../../ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "../../../ui/sheet";
import EDSThemeToggle from "../../ThemeToggle/EDSThemeToggle";

export const MenuButton = () => {
  const userContext = useUserContext();
  const [debugCounter, setDebugCounter] = useState(0);
  const {
    isVisible: isSupportVisible,
    toggleVisibility: toggleSupportVisibility
  } = useIntercomVisibility();

  const device = getDevice();
  const { variant } = useHeaderFlags();
  const { colourScheme, setTheme } = useDeviceContext();

  const showMobileMenu = device !== "desktop";

  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const goTo = (page: string) => {
    setOpen(false);
    navigate(`${page}`);
  };

  const openMenu = () => {
    setOpen(true);
  };

  const handleThemeToggle = async (isDarkMode: boolean) => {
    const themeName = isDarkMode ? "Dark" : "Light";
    setTheme(themeName);
    await updateColourScheme(themeName);
  };

  if (!showMobileMenu) {
    return null;
  }

  const userInitials =
    (userContext.user?.first_name ?? "").substring(0, 1) +
    (userContext.user?.last_name ?? "").substring(0, 1);
  const userName =
    `${userContext.user?.first_name ?? ""} ${userContext.user?.last_name ?? ""}`.trim();
  const userEmail = userContext.user?.email;

  if (variant === "v2") {
    return (
      <div className="MenuButton ml-3">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button name="Main menu" fill="clear" onClick={openMenu}>
              <img
                src={MenuIcon}
                className="MenuIcon h-3 w-4 mt-[3px]"
                alt="MenuIcon"
              />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[320px] sm:w-[360px] p-0 flex flex-col [&>button.absolute]:!top-[calc(env(safe-area-inset-top)+1rem)]"
          >
            {/* Brand head — top padding clears the iPad status bar + native
                header so the logo/close don't collide with the device chrome. */}
            <div className="flex items-center px-6 pb-4 border-b border-border pt-[calc(env(safe-area-inset-top)+1.25rem)]">
              <img
                src={
                  colourScheme === "dark"
                    ? AccessCollinsLogoGenericDark
                    : AccessCollinsLogoGenericLight
                }
                alt="Access Collins"
                className="h-6"
              />
            </div>

            {/* Primary nav */}
            <nav className="flex flex-col gap-1 p-3">
              <button
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent text-sm font-medium"
                onClick={() => goTo("/diary")}
              >
                <Calendar className="size-[18px] text-foreground" />
                <span>Diary</span>
              </button>
              <button
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent text-sm font-medium"
                onClick={() => goTo("/enquiries")}
              >
                <FileText className="size-[18px] text-foreground" />
                <span>Enquiries</span>
              </button>
              <button
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent text-sm font-medium"
                onClick={() => goTo("/settings")}
              >
                <Settings className="size-[18px] text-foreground" />
                <span>Settings</span>
              </button>
              <button
                className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent text-sm font-medium"
                onClick={toggleSupportVisibility}
              >
                <Headphones className="size-[18px] text-foreground" />
                <span>
                  {isSupportVisible ? "Hide Support" : "Show Support"}
                </span>
              </button>
            </nav>

            {/* Appearance */}
            <div className="px-6 py-4 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                Appearance
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Theme</span>
                <EDSThemeToggle
                  isDarkMode={colourScheme === "dark"}
                  onToggle={handleThemeToggle}
                />
              </div>
            </div>

            {/* Account anchored at bottom */}
            <div className="mt-auto border-t border-border">
              {!isNativeTab() && (
                <button
                  className="flex items-center gap-3 w-full px-6 py-4 hover:bg-accent text-left"
                  onClick={dmnIdentity.signOut}
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback>{userInitials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {userName}
                    </div>
                    {userEmail && (
                      <div className="text-xs text-muted-foreground truncate">
                        {userEmail}
                      </div>
                    )}
                  </div>
                  <LogOut className="size-4 text-muted-foreground shrink-0" />
                </button>
              )}
            </div>

            {/* Hidden debug trigger — 10 taps */}
            <button
              style={{ opacity: 0, height: 0, padding: 0, margin: 0 }}
              tabIndex={-1}
              onClick={() => {
                setDebugCounter(debugCounter + 1);
                if (debugCounter > 9) {
                  navigate("/debug");
                  setOpen(false);
                  setDebugCounter(0);
                }
              }}
            />
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className="MenuButton ml-3">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button name="Main menu" fill="clear" onClick={openMenu}>
            <img
              src={MenuIcon}
              className="MenuIcon h-3 w-4 mt-[3px]"
              alt="MenuIcon"
            />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] sm:w-[400px]">
          {!isNativeTab() ? (
            <div className="user-profile mb-6 ml-[var(--panelPadding)]">
              <Avatar className="user-profile__avatar h-[60px] w-[60px]">
                <AvatarFallback>
                  {(userContext.user?.first_name ?? "").substring(0, 1) +
                    (userContext.user?.last_name ?? "").substring(0, 1)}
                </AvatarFallback>
              </Avatar>
              <div className="user-profile__text mt-2">
                <h3 className="user-profile__name font-semibold">
                  {`${userContext.user?.first_name} ${userContext.user?.last_name}`.trim()}
                </h3>
                {userContext.user?.email && (
                  <p className="user-profile__email text-sm text-muted-foreground">
                    {userContext.user?.email}
                  </p>
                )}
              </div>
            </div>
          ) : null}

          <div className="flex flex-col space-y-2">
            <button
              className="flex items-center space-x-3 p-3 rounded-md hover:bg-accent"
              onClick={() => goTo("/diary")}
            >
              <Calendar className="size-5 text-green-600" />
              <span>Diary</span>
            </button>
            <button
              className="flex items-center space-x-3 p-3 rounded-md hover:bg-accent"
              onClick={() => goTo("/enquiries")}
            >
              <Calendar className="size-5 text-green-600" />
              <span>Enquiries</span>
            </button>
            <button
              className="flex items-center space-x-3 p-3 rounded-md hover:bg-accent"
              onClick={() => goTo("/settings")}
            >
              <Settings className="size-5 text-green-600" />
              <span>Settings</span>
            </button>
            <button
              className="flex items-center space-x-3 p-3 rounded-md hover:bg-accent"
              onClick={toggleSupportVisibility}
            >
              <Headphones className="size-5 text-green-600" />
              <span>{isSupportVisible ? "Hide Support" : "Show Support"}</span>
            </button>
            {!isNativeTab() ? (
              <button
                className="flex items-center space-x-3 p-3 rounded-md hover:bg-accent"
                onClick={dmnIdentity.signOut}
              >
                <LogOut className="size-5 text-green-600" />
                <span>Sign Out</span>
              </button>
            ) : null}
            <button
              style={{ opacity: 0 }}
              className="flex items-center space-x-3 p-3 rounded-md hover:bg-accent"
              onClick={() => {
                setDebugCounter(debugCounter + 1);

                if (debugCounter > 9) {
                  navigate("/debug");
                  setOpen(false);
                  setDebugCounter(0);
                }
              }}
            >
              <span>Show debug</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
